import {
  ArrowUpRight,
  BookOpen,
  Calculator,
  GraduationCap,
  HelpCircle,
  ImageIcon,
  LayoutDashboard,
  Shapes,
  Swords,
  Table2,
  TrendingUp,
  Triangle,
  Video,
  VideoOff,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Trousse } from '@/features/trousse/trousse';
import type { TrousseItem } from '@/features/trousse/trousse-items';
import { useTrousseTools } from '@/features/trousse/trousse-tools';

export interface QuickNavItem {
  id: string;
  label: string;
  icon: LucideIcon;
  scrollBlock?: ScrollLogicalPosition;
}

function scrollToAnchor(id: string, block: ScrollLogicalPosition = 'start') {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block });
}

export function getBlockIcon(type: string): LucideIcon {
  switch (type) {
    case 'function_graph':
      return TrendingUp;
    case 'geometry_figure':
      return Triangle;
    case 'vector_diagram':
      return ArrowUpRight;
    case 'table':
      return Table2;
    case 'image':
      return ImageIcon;
    default:
      return LayoutDashboard;
  }
}

export function ExerciseQuickNav({
  items,
  onShare,
  onCalculator,
  onLesson,
  onToolbox,
  onStream,
  streaming = false,
  variant = 'float',
  extraTools = [],
  onKitToggle,
  tuck = true,
  collection = true,
  kitTabs = true,
}: {
  items: QuickNavItem[];
  /**
   * `float` : colonne flottante à droite (historique) ; `bar` : la TROUSSE —
   * exercice « sur la feuille », tout entre dans un objet fixe au bord droit et
   * la feuille garde toute la largeur ; `row` : barre horizontale dans le flux
   * (écran qui a déjà sa propre barre collée, ex. Live).
   */
  variant?: 'float' | 'bar' | 'row';
  onShare?: () => void;
  onCalculator?: () => void;
  /** Ouvre le cours (SKILLCARDS de la question courante). */
  onLesson?: () => void;
  /** Ouvre la Boîte à outils (outils choisis selon le niveau et le domaine). */
  onToolbox?: () => void;
  /** Se filmer pendant l'exercice (défi streamé) : propose de démarrer, ou coupe s'il filme. */
  onStream?: () => void;
  /** L'enregistrement est en cours (bouton rouge, « Couper »). */
  streaming?: boolean;
  /** Outils de la feuille à mettre dans la trousse (tableau à dresser, arbre, corrigé). */
  extraTools?: TrousseItem[];
  /** La trousse s'ouvre ou se referme (la démo guidée la fait découvrir). */
  onKitToggle?: (open: boolean) => void;
  /** La trousse se cache aux 3/4 au bord droit (défaut) ; `false` : toujours sortie. */
  tuck?: boolean;
  /** le lien « Ma collection de trousses » dans la trousse (faux : la démo guidée ne montre que les Moves) */
  collection?: boolean;
  /** les onglets Moves / Cours / Outils de la trousse (faux : la démo guidée, qui n'a que des Moves) */
  kitTabs?: boolean;
}) {
  // les outils que la feuille publie (tableau à dresser, arbre, corrigé) : vide ailleurs
  const sheetTools = useTrousseTools();

  // Exercice « sur la feuille » : plus de barre en haut, tout entre dans la trousse — elle est fixe,
  // donc la feuille garde toute la largeur de l'écran (maquette clavier v3). ⚠️ Elle n'est PAS
  // soumise à la garde ci-dessous (« moins de deux ancres ») : la trousse est le meuble de l'élève,
  // et son contenu peut ne venir que de la feuille (`useTrousseTools`) ou de ses Moves.
  if (variant === 'bar') {
    const tools: TrousseItem[] = [];
    if (onLesson)
      tools.push({
        id: 'lesson',
        label: 'Cours',
        icon: GraduationCap,
        group: 'help',
        onPick: onLesson,
      });
    if (onCalculator)
      tools.push({
        id: 'calc',
        label: 'Calculatrice',
        icon: Calculator,
        group: 'help',
        onPick: onCalculator,
      });
    if (onToolbox)
      tools.push({
        id: 'toolbox',
        label: 'Boîte à outils',
        icon: Shapes,
        group: 'help',
        onPick: onToolbox,
      });
    for (const item of items)
      tools.push({
        id: item.id,
        label: item.label,
        icon: item.icon,
        group: 'anchor',
        onPick: () => scrollToAnchor(item.id, item.scrollBlock),
      });
    if (onShare)
      tools.push({
        id: 'share',
        label: 'Lancer un défi',
        icon: Swords,
        group: 'share',
        onPick: onShare,
      });
    if (onStream)
      tools.push({
        id: 'stream',
        label: streaming ? 'Couper la caméra' : 'Me filmer',
        icon: streaming ? VideoOff : Video,
        group: 'share',
        active: streaming,
        onPick: onStream,
      });
    const all = [...sheetTools, ...tools, ...extraTools];
    // vraiment rien dedans, pas même le lien de la collection : pas de meuble vide à l'écran
    if (!all.length && !collection) return null;
    return (
      <Trousse
        items={all}
        onToggle={onKitToggle}
        tuck={tuck}
        collection={collection}
        tabs={kitTabs}
      />
    );
  }

  if (items.length < 2 && !onShare && !onCalculator && !onLesson && !onToolbox && !onStream)
    return null;

  return (
    <nav
      aria-label="Navigation rapide"
      className={
        variant === 'row'
          ? 'flex flex-row flex-wrap justify-end gap-1 px-3 py-1'
          : 'native:top-[calc(env(safe-area-inset-top)+0.625rem)] fixed right-4 top-2.5 z-40 flex flex-col gap-1 sm:right-4'
      }
    >
      {onShare && (
        <button
          type="button"
          title="Lancer un défi"
          onClick={onShare}
          className="bg-primary-500 hover:bg-primary-600 flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-full text-white transition-colors active:scale-[0.98]"
        >
          <Swords className="h-5 w-5 shrink-0" aria-hidden />
          <span className="hidden text-sm font-medium sm:block">Défi</span>
        </button>
      )}
      {onStream && (
        <button
          type="button"
          title={streaming ? 'Couper la caméra' : 'Me filmer'}
          aria-label={streaming ? 'Couper la caméra' : 'Me filmer pendant l’exercice'}
          aria-pressed={streaming}
          onClick={onStream}
          className={
            streaming
              ? 'flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-full bg-red-600 text-white transition-colors active:scale-[0.98] sm:px-3'
              : 'text-ink-700 hover:bg-ink-100 hover:text-ink-900 flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl bg-white/40 p-2.5 backdrop-blur-2xl transition-colors active:scale-[0.98] sm:pr-4'
          }
        >
          {streaming ? (
            <VideoOff className="h-5 w-5 shrink-0" aria-hidden />
          ) : (
            <Video className="h-5 w-5 shrink-0" aria-hidden />
          )}
          <span className="hidden text-sm font-medium sm:block">
            {streaming ? 'Couper' : 'Me filmer'}
          </span>
        </button>
      )}
      {onLesson && (
        <button
          type="button"
          title="Cours"
          onClick={onLesson}
          className="bg-xp-100 text-xp-800 hover:bg-xp-200 flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl p-2.5 transition-colors active:scale-[0.98] sm:pr-4"
        >
          <GraduationCap className="h-5 w-5 shrink-0" />
          <span className="hidden text-sm font-medium sm:block">Cours</span>
        </button>
      )}
      {onCalculator && (
        <button
          type="button"
          title="Calculatrice"
          onClick={onCalculator}
          className="text-ink-700 hover:bg-ink-100 hover:text-ink-900 flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl bg-white/40 p-2.5 backdrop-blur-2xl transition-colors active:scale-[0.98] sm:pr-4"
        >
          <Calculator className="h-5 w-5 shrink-0" />
          <span className="hidden text-sm font-medium sm:block">Calculatrice</span>
        </button>
      )}
      {onToolbox && (
        <button
          type="button"
          title="Boîte à outils"
          onClick={onToolbox}
          className="text-ink-700 hover:bg-ink-100 hover:text-ink-900 flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl bg-white/40 p-2.5 backdrop-blur-2xl transition-colors active:scale-[0.98] sm:pr-4"
        >
          <Shapes className="h-5 w-5 shrink-0" />
          <span className="hidden text-sm font-medium sm:block">Outils</span>
        </button>
      )}
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.id}
            type="button"
            title={item.label}
            onClick={() => scrollToAnchor(item.id, item.scrollBlock)}
            className="text-ink-500 hover:bg-ink-100 hover:text-ink-900 flex min-h-11 min-w-11 items-center justify-center gap-2 rounded-xl bg-white/20 p-2.5 backdrop-blur-2xl transition-colors active:scale-[0.98] sm:pr-4"
          >
            <Icon className="h-5 w-5 shrink-0" />
            <span className="hidden text-sm font-medium sm:block">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

export { BookOpen, HelpCircle };
